/**
 * @license
 * Copyright 厦门乾元盛世科技有限公司 All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file.
 */


import { Component, Input, Output, EventEmitter, ElementRef, Renderer, ViewEncapsulation } from '@angular/core';
import { OnInit, OnDestroy, HostBinding, HostListener, Host, Optional, forwardRef, Inject, SkipSelf } from '@angular/core';
import KeyCode from '../util/key.code';
import classNames from 'classnames';

import { MenuItem } from './menu.item';
import { Menu } from './menu';
/**
 * 子菜单
 * 参考: https://github.com/react-component/menu
 */
export class SubMenu /*extends Menu*/ implements OnInit, OnDestroy {
public isMenuItem = 0;
public isSubMenu = 1;
/**
 * 用户CSS样式
 */
class: string;

    // tslint:disable-next-line:no-input-rename
     eventKey: string;
/**
 * 模式的菜单缩进宽度，默认为24px
 */
inlineIndent = 24;

     disabled = false;

     open = false;

     defaultActiveFirst = true;
/**
 * menuItem 被点击时的事件，参数为：({ item, key })
 */
titleClick = new EventEmitter<{ key: string, domEvent: Event }>();
/**
 * menuItem 事件，参数为：({ item, key })
 */
titleMouseEnter = new EventEmitter<{ key: string, domEvent: Event }>();
/**
 * menuItem 事件，参数为：({ item, key })
 */
titleMouseLeave = new EventEmitter<{ key: string, domEvent: Event }>();
public level = 1;

    protected instanceArray: Array<MenuItem> = [];
/**
 * 鼠标移出的一定时间后(如100毫秒)，再执行隐藏子菜单的操作
 */
private mouseLeaveTimer: any;
private mouseLeaveTitleTimer: any;
/**
 * @param {?} renderer
 * @param {?} elementRef
 * @param {?} parentMenu
 * @param {?} submenu
 */
constructor(
private renderer: Renderer,
private elementRef: ElementRef,
private parentMenu: Menu,
private submenu: SubMenu) {
    }
/**
 * @return {?}
 */
get active(): boolean {
        return !this.disabled && this.parentMenu.activeKey === this.eventKey;
    }
/**
 * @return {?}
 */
get mode(): string {
        return this.parentMenu && this.parentMenu.mode;
    }
/**
 * @return {?}
 */
get openSubMenuOnMouseEnter() {
        return this.mode === 'inline' ? false : true;
    }
/**
 * @return {?}
 */
get closeSubMenuOnMouseLeave() {
        return this.mode === 'inline' ? false : true;
    }
/**
 * 样式前缀
 * @return {?}
 */
get prefixCls(): string {
        return this.parentMenu && this.parentMenu.prefixCls || '';
    }
/**
 * @return {?}
 */
ngOnInit(): void {
        if (this.submenu !== null) {
            this.level = this.submenu.level + 1;
        }
    }
/**
 * @return {?}
 */
ngOnDestroy(): void {
        const /** @type {?} */ parentMenu: Menu = this.parentMenu;
        this.clearMouseLeaveTimer();
        if (parentMenu) {
            parentMenu.onDestroy(this.eventKey);
        }
    }
/**
 * 菜单样式
 * @return {?}
 */
get classes(): string {
        const /** @type {?} */ classes = classNames(`${this.prefixCls}-submenu`, {
            [`${this.prefixCls}-submenu-${this.mode}`]: this.mode,
            [`${this.prefixCls}-submenu-active`]: !this.disabled && this.active,
            [`${this.prefixCls}-submenu-selected`]: this.isChildrenSelected(),
            [`${this.prefixCls}-submenu-open`]: this.isOpen(),
            [`${this.prefixCls}-submenu-disabled`]: this.disabled
        }, this.class);
        return classes;
    }
/**
 * @return {?}
 */
get titlePaddingLeft(): string {
        if (this.mode === 'inline') {
            return (this.inlineIndent * this.level) + 'px';
        }
        return null;
    }
/**
 * @return {?}
 */
get expanded(): boolean {
        return this.isOpen();
    }
/**
 * 菜单样式
 * @return {?}
 */
get titleClasses(): string {
        const /** @type {?} */ classes = classNames({
            [`${this.prefixCls}-submenu-title`]: 1
        });
        return classes;
    }
/**
 * @return {?}
 */
get subClasses(): string {
        const /** @type {?} */ visible = this.isOpen();
        const /** @type {?} */ mode = this.mode === 'horizontal' ? 'vertical' : this.mode;
        const /** @type {?} */ classes = classNames(this.prefixCls, {
            [`${this.prefixCls}-${mode}`]: mode,
            [`${this.prefixCls}-hidden`]: !visible,
            [`${this.prefixCls}-sub`]: true
        });
        return classes;
    }
/**
 * The underlying host native element
 * @return {?}
 */
getHostElement(): HTMLElement {
        return /** @type {?} */(( this.elementRef.nativeElement as HTMLElement));
    }
/**
 * @param {?} event
 * @return {?}
 */
onKeyDown(event: KeyboardEvent): void {
        const /** @type {?} */ keyCode = event.keyCode;
        const /** @type {?} */ isOpen = this.isOpen();
        const /** @type {?} */ parentMenu: Menu = this.parentMenu;

        if (keyCode === KeyCode.ENTER) {
            this.onTitleClick(event);
            this.defaultActiveFirst = true;
            return;
        }

        if (keyCode === KeyCode.RIGHT) {
            if (!isOpen) {
                this.triggerOpenChange(true);
                this.defaultActiveFirst = true;
            }
            return;
        }
        if (keyCode === KeyCode.LEFT) {
            if (isOpen) {
                this.triggerOpenChange(false);
            }
            return;
        }

        if (isOpen && (keyCode === KeyCode.UP || keyCode === KeyCode.DOWN)) {
            if (parentMenu) {
                parentMenu.onKeyDown(event);
            }
        }
    }
/**
 * @param {?} event
 * @return {?}
 */
onMouseEnter(event: MouseEvent): void {
        const /** @type {?} */ parentMenu: Menu = this.parentMenu;
        this.clearMouseLeaveSubMenuTimer();

        parentMenu.onMouseEnter({
            key: this.eventKey,
            item: this,
            domEvent: event,
            trigger: 'mouseenter'
        });
    }
/**
 * @param {?} event
 * @return {?}
 */
onMouseLeave(event: MouseEvent): void {
        const /** @type {?} */ parentMenu: Menu = this.parentMenu;

        // prevent popup menu and submenu gap
        this.mouseLeaveTimer = setTimeout(() => {
            // leave whole sub tree
            // still active
            if (this.mode !== 'inline') {
                const /** @type {?} */ isOpen = this.isOpen();
                if (isOpen && this.closeSubMenuOnMouseLeave && this.active) {
                    parentMenu.onItemHover({
                        key: this.eventKey,
                        item: this,
                        hover: false,
                        trigger: 'mouseleave',
                        domEvent: event,
                        openChanges: [{
                            key: this.eventKey,
                            item: this,
                            trigger: 'mouseleave',
                            open: false
                        }]
                    });
                } else {
                    if (this.active) {
                        parentMenu.onItemHover({
                            key: this.eventKey,
                            item: this,
                            hover: false,
                            trigger: 'mouseleave',
                            domEvent: event
                        });
                    }
                    if (isOpen && this.closeSubMenuOnMouseLeave) {
                        this.triggerOpenChange(false);
                    }
                }
            }
        }, 100);

        // trigger mouseleave
        parentMenu.onMouseLeave({
            key: this.eventKey,
            item: this,
            domEvent: event,
            trigger: 'mouseleave'
        });
    }
/**
 * @param {?} event
 * @return {?}
 */
onTitleMouseEnter(event: Event): void {
        const /** @type {?} */ parentMenu: Menu = this.parentMenu;
        const /** @type {?} */ eventKey: string = this.eventKey;

        this.clearMouseLeaveTitleTimer();

        const /** @type {?} */ openChanges = [];
        if (this.openSubMenuOnMouseEnter) {
            openChanges.push({
                key: eventKey,
                item: this,
                trigger: 'mouseenter',
                open: true
            });
        }
        parentMenu.onItemHover({
            key: eventKey,
            item: this,
            hover: true,
            trigger: 'mouseenter',
            domEvent: event,
            openChanges
        });
        this.defaultActiveFirst = false;

        this.titleMouseEnter.emit({
            key: eventKey,
            domEvent: event
        });
    }
/**
 * @param {?} event
 * @return {?}
 */
onTitleMouseLeave(event: Event): void {
        const /** @type {?} */ parentMenu: Menu = this.parentMenu;
        const /** @type {?} */ eventKey: string = this.eventKey;

        this.mouseLeaveTitleTimer = setTimeout(() => {
            if (this.mode === 'inline' && this.active) {
                parentMenu.onItemHover({
                    key: eventKey,
                    item: this,
                    hover: false,
                    trigger: 'mouseleave',
                    domEvent: event
                });
            }
            this.titleMouseLeave.emit({
                key: eventKey,
                domEvent: event
            });
        }, 100);
    }
/**
 * @param {?} event
 * @return {?}
 */
onTitleClick(event: Event): void {
        this.titleClick.emit({
            key: this.eventKey,
            domEvent: event
        });

        if (this.openSubMenuOnMouseEnter) {
            return;
        }
        this.triggerOpenChange(!this.isOpen(), 'click');
        this.defaultActiveFirst = false;
    }
/**
 * @return {?}
 */
getKeyPath(): string[] {
        const /** @type {?} */ keyPath: string[] = this.submenu && this.submenu.getKeyPath() || [];
        return keyPath.concat(this.eventKey);
    }
/**
 * @param {?} open
 * @param {?=} type
 * @return {?}
 */
triggerOpenChange(open: boolean, type?: string): void {
        const /** @type {?} */ key = this.eventKey;
        const /** @type {?} */ parentMenu: Menu = this.parentMenu;
        parentMenu.onOpenChange({
            key,
            item: this,
            trigger: type,
            open,
        });
    }
/**
 * @return {?}
 */
isChildrenSelected(): boolean {
        let /** @type {?} */ find = false;
        const /** @type {?} */ selectedKeys: string[] = this.parentMenu.selectedKeys;
        this.instanceArray.forEach(item => {
            if (selectedKeys.indexOf(item.eventKey) !== -1) {
                return !(find = true);
            }
        });
        return find;
    }
/**
 * @return {?}
 */
isOpen(): boolean {
        return this.parentMenu.openKeys.indexOf(this.eventKey) !== -1;
    }
/**
 * @param {?} item
 * @return {?}
 */
registerItem(item: MenuItem): void {
        this.instanceArray.push(item);
    }
/**
 * @return {?}
 */
clearMouseLeaveTimer(): void {
        this.clearMouseLeaveSubMenuTimer();
        this.clearMouseLeaveTitleTimer();
    }
/**
 * @return {?}
 */
clearMouseLeaveSubMenuTimer(): void {
        if (this.mouseLeaveTimer) {
            clearTimeout(this.mouseLeaveTimer);
            this.mouseLeaveTimer = null;
        }
    }
/**
 * @return {?}
 */
clearMouseLeaveTitleTimer(): void {
        if (this.mouseLeaveTitleTimer) {
            clearTimeout(this.mouseLeaveTitleTimer);
            this.mouseLeaveTitleTimer = null;
        }
    }

static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
    selector: 'ant-submenu',
    template: `
        <div [class]="titleClasses" aria-haspopup="true"
             [style.paddingLeft]="titlePaddingLeft" [attr.aria-expanded]="expanded"
             (mouseenter)="onTitleMouseEnter($event)" (mouseleave)="onTitleMouseLeave($event)" (click)="onTitleClick($event)">
            <ng-content select="[ant-submenu-title]"></ng-content>
        </div>
        <div [class]="subClasses" role="menu" aria-activedescendant="">
            <ng-content></ng-content>
        </div>
    `,
    encapsulation: ViewEncapsulation.None
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Renderer, },
{type: ElementRef, },
{type: Menu, decorators: [{ type: Inject, args: [forwardRef(() => Menu), ] }, { type: Optional }, { type: Host }, ]},
{type: SubMenu, decorators: [{ type: Optional }, { type: Host }, { type: SkipSelf }, ]},
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'class': [{ type: Input },],
'eventKey': [{ type: Input, args: ['key', ] },],
'inlineIndent': [{ type: Input },],
'disabled': [{ type: Input },],
'open': [{ type: Input },],
'defaultActiveFirst': [{ type: Input },],
'titleClick': [{ type: Output },],
'titleMouseEnter': [{ type: Output },],
'titleMouseLeave': [{ type: Output },],
'classes': [{ type: HostBinding, args: ['class', ] },],
'onKeyDown': [{ type: HostListener, args: ['keydown', ['$event'], ] },],
'onMouseEnter': [{ type: HostListener, args: ['mouseenter', ['$event'], ] },],
'onMouseLeave': [{ type: HostListener, args: ['mouseleave', ['$event'], ] },],
};
}

function SubMenu_tsickle_Closure_declarations() {
/** @type {?} */
SubMenu.decorators;
/**
 * @nocollapse
 * @type {?}
 */
SubMenu.ctorParameters;
/** @type {?} */
SubMenu.propDecorators;
/** @type {?} */
SubMenu.prototype.isMenuItem;
/** @type {?} */
SubMenu.prototype.isSubMenu;
/**
 * 用户CSS样式
 * @type {?}
 */
SubMenu.prototype.class;
/** @type {?} */
SubMenu.prototype.eventKey;
/**
 * 模式的菜单缩进宽度，默认为24px
 * @type {?}
 */
SubMenu.prototype.inlineIndent;
/** @type {?} */
SubMenu.prototype.disabled;
/** @type {?} */
SubMenu.prototype.open;
/** @type {?} */
SubMenu.prototype.defaultActiveFirst;
/**
 * menuItem 被点击时的事件，参数为：({ item, key })
 * @type {?}
 */
SubMenu.prototype.titleClick;
/**
 * menuItem 事件，参数为：({ item, key })
 * @type {?}
 */
SubMenu.prototype.titleMouseEnter;
/**
 * menuItem 事件，参数为：({ item, key })
 * @type {?}
 */
SubMenu.prototype.titleMouseLeave;
/** @type {?} */
SubMenu.prototype.level;
/** @type {?} */
SubMenu.prototype.instanceArray;
/**
 * 鼠标移出的一定时间后(如100毫秒)，再执行隐藏子菜单的操作
 * @type {?}
 */
SubMenu.prototype.mouseLeaveTimer;
/** @type {?} */
SubMenu.prototype.mouseLeaveTitleTimer;
/** @type {?} */
SubMenu.prototype.renderer;
/** @type {?} */
SubMenu.prototype.elementRef;
/** @type {?} */
SubMenu.prototype.parentMenu;
/** @type {?} */
SubMenu.prototype.submenu;
}



interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
